import { create } from 'zustand';
import { useViewerStore } from './viewer-store';
import { authenticatedCacheGeneration, onAuthenticatedSessionChange } from '@/shared/lib/authenticated-session';
import type {
  FillOpDropPayload,
  FillOpPointerDropDetail,
  FillOpPointerHoverDetail,
} from '@/features/modules/drag-drop';
import type { ModuleDropPayload } from '@/features/modules/types';
import type { CaptureRequest } from '@/features/scene/types';

type CommandStatus = 'pending' | 'running';
type ModuleAddRoute = 'viewport' | 'room';
export type SelectionDragPreview = {
  entityId: string;
  delta: [number, number, number] | null;
};

type CommandBase = {
  id: string;
  status: CommandStatus;
  /* 'room' — вставка в комнату квартиры. Владелец живёт в оболочке приложения, а не в панели
     «Структура»: та по умолчанию закрыта, и команда висела бы в очереди молча. */
  owner?: 'scene' | 'apartment' | 'room';
  createdAt: number;
  /**
   * VF-FE-14: штамп идентичности модели в момент постановки в очередь.
   * Pending-команда без привязки применялась к ЧУЖОМУ active model: владелец
   * (SceneCanvas) разобрал очередь уже после смены модели — драг из каталога
   * уезжал в новую сцену. Очередь ставит штамп при записи; команды,
   * потерявшие штамп, отклоняются. Эпоха отличает повторный вход A→B→A,
   * а ключ сессии — перезапуск страницы.
   */
  identity?: SceneCommandIdentity | null;
};

export type SceneCommandIdentity = {
  modelId: string;
  revisionId: string | null;
  sessionId: string;
  modelEpoch?: number;
  authGeneration?: number;
};

/**
 * Идентичность сцены, под которую штампуются команды: активная модель,
 * ревизия её бандла (bundleHash прочнее bundleVersion — счётчик может
 * совпасть у разных баз) и сессия страницы. Сессия ленивая: одна
 * на вкладку, читается по первому штампу.
 */
export function sceneCommandIdentity(
  modelId: string | null | undefined,
  bundle: { bundleVersion?: number; bundleHash?: string } | null | undefined,
): SceneCommandIdentity | null {
  if (!modelId) return null;
  let sessionId: string | null = null;
  try {
    sessionId = window.sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = `sess-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(SESSION_KEY, sessionId);
    }
  } catch {
    sessionId = null;
  }
  return {
    modelId,
    revisionId: bundle?.bundleHash ?? (bundle?.bundleVersion != null ? String(bundle.bundleVersion) : null),
    sessionId: sessionId ?? 'no-session-storage',
    modelEpoch: useViewerStore.getState().activeModelEpoch,
    authGeneration: authenticatedCacheGeneration(),
  };
}

const SESSION_KEY = 'holzcom.scene-command.session.v1';

/** Own an asynchronous scene mutation through both server and refetch waits. */
export async function runSceneOwnedMutation<T>(
  identity: SceneCommandIdentity | null,
  operation: () => Promise<T>,
  invalidate: () => Promise<unknown>,
  applyResult: (result: T) => void,
): Promise<T | undefined> {
  const current = () => commandIdentityMatches({ identity },
    sceneCommandIdentity(useViewerStore.getState().activeModelId, null));
  if (!current()) throw new Error('SCENE_COMMAND_OWNER_CHANGED');
  const result = await operation();
  if (!current()) return;
  await invalidate();
  if (!current()) return;
  applyResult(result);
  return result;
}

/** Сличить штамп команды с текущей идентичностью.
 *
 * Gate is на МОДЕЛИ и СЕССИИ — не на ревизии бандла. Ревизия (bundleHash) в
 * очередь-гейте была бы ловушкой: собственный commit команды меняет revision
 * активой модели, а инвализация бандла прилетает до разбора следующего элемента
 * очереди. Строгое равенство ревизий молча выбрасывало rapid-add'ы (2-й клик в
 * каталоге «отбрасывался» как «чужая сцена», хотя модель не менялась). Каскад
 * версий делает серверный CAS у каждой мутации; клиентский drain обязан лишь
 * не применить команду к ДРУГОЙ модели/сессии.
 * Команда без штампа не может быть принята владельцем. */
export function commandIdentityMatches(
  command: { identity?: SceneCommandIdentity | null },
  current: SceneCommandIdentity | null,
): boolean {
  const stamp = command.identity;
  if (!stamp) return false;
  if (!current) return false; // штамп есть, а текущей сцены ещё нет — подождёт
  return stamp.modelId === current.modelId
    && stamp.authGeneration === current.authGeneration
    && stamp.authGeneration === authenticatedCacheGeneration()
    && stamp.modelEpoch === current.modelEpoch
    && (stamp.sessionId === current.sessionId
      || stamp.sessionId === 'no-session-storage'
      || current.sessionId === 'no-session-storage');
}

export type SceneCommand = CommandBase & (
  | { kind: 'module.add'; route: ModuleAddRoute; payload: ModuleDropPayload }
  | { kind: 'module.replace'; payload: ModuleDropPayload }
  | { kind: 'fill.pointer-drop'; detail: FillOpPointerDropDetail }
  | { kind: 'render.capture'; request: Omit<CaptureRequest, 'id'>; photoSet: boolean }
  | { kind: 'view.snapshot'; label: string; projection: string }
  | { kind: 'view.restore'; viewId: string }
  | { kind: 'view.remove'; viewId: string }
);

type SceneCommandInput =
  | { kind: 'module.add'; route: ModuleAddRoute; payload: ModuleDropPayload }
  | { kind: 'module.replace'; payload: ModuleDropPayload }
  | { kind: 'fill.pointer-drop'; detail: FillOpPointerDropDetail }
  | { kind: 'render.capture'; request: Omit<CaptureRequest, 'id'>; photoSet: boolean }
  | { kind: 'view.snapshot'; label: string; projection: string }
  | { kind: 'view.restore'; viewId: string }
  | { kind: 'view.remove'; viewId: string };

type SceneCommandState = {
  commands: SceneCommand[];
  revision: number;
  fillHover: FillOpPointerHoverDetail | null;
  selectionDragPreview: SelectionDragPreview | null;
  lastFailure: { commandId: string; kind: SceneCommand['kind'] | null; message: string } | null;
  enqueue: (input: SceneCommandInput, identity?: SceneCommandIdentity | null) => string;
  claim: (commandId: string, owner: 'scene' | 'apartment' | 'room') => boolean;
  complete: (commandId: string) => void;
  fail: (commandId: string, error: unknown) => void;
  rerouteModuleAdd: (commandId: string, route: ModuleAddRoute) => void;
  /** Отметить, что отказ уже показан человеку. Без этого он копится молча. */
  acknowledgeFailure: (commandId: string) => void;
  setFillHover: (detail: FillOpPointerHoverDetail | null) => void;
  setSelectionDragPreview: (detail: SelectionDragPreview | null) => void;
  clear: () => void;
};

let commandSequence = 0;

function commandId() {
  commandSequence += 1;
  return `scene-command-${Date.now().toString(36)}-${commandSequence.toString(36)}`;
}

export const useSceneCommandStore = create<SceneCommandState>((set, get) => ({
  commands: [],
  revision: 0,
  fillHover: null,
  selectionDragPreview: null,
  lastFailure: null,
  enqueue: (input, identity) => {
    const stamp = identity ?? sceneCommandIdentity(useViewerStore.getState().activeModelId, null);
    if (!stamp && ['module.add', 'module.replace', 'fill.pointer-drop'].includes(input.kind)) {
      throw new Error('SCENE_COMMAND_IDENTITY_REQUIRED');
    }
    const id = commandId();
    const command = {
      ...input,
      id,
      status: 'pending' as const,
      createdAt: Date.now(),
      /* VF-FE-14: штамп ставится ПРИ ЗАПИСИ, не при разборе — владелец мог
       * начать разбор очереди уже с другой моделью. */
      identity: stamp,
    } as SceneCommand;
    set((state) => ({
      commands: [...state.commands, command],
      revision: state.revision + 1,
    }));
    return id;
  },
  claim: (id, owner) => {
    const command = get().commands.find((item) => item.id === id);
    if (!command || command.status !== 'pending') return false;
    set((state) => ({
      commands: state.commands.map((item) => (
        item.id === id ? { ...item, status: 'running' as const, owner } : item
      )),
      revision: state.revision + 1,
    }));
    return true;
  },
  complete: (id) => set((state) => ({
    commands: state.commands.filter((item) => item.id !== id),
    revision: state.revision + 1,
  })),
  fail: (id, error) => set((state) => state.commands.some((item) => item.id === id) ? ({
    commands: state.commands.filter((item) => item.id !== id),
    revision: state.revision + 1,
    lastFailure: {
      commandId: id,
      /* Род команды снимается ДО удаления её из очереди: без него человеку показывается
         безымянный текст ошибки, из которого не видно, что именно не получилось. */
      kind: state.commands.find((item) => item.id === id)?.kind ?? null,
      message: error instanceof Error ? error.message : String(error),
    },
  }) : {}),
  acknowledgeFailure: (id) => set((state) => (
    state.lastFailure && state.lastFailure.commandId === id ? { lastFailure: null } : {}
  )),
  rerouteModuleAdd: (id, route) => set((state) => ({
    commands: state.commands.map((item) => (
      item.id === id && item.kind === 'module.add'
        ? { ...item, route, status: 'pending' as const, owner: undefined }
        : item
    )),
    revision: state.revision + 1,
  })),
  setFillHover: (fillHover) => set({ fillHover }),
  setSelectionDragPreview: (selectionDragPreview) => set({ selectionDragPreview }),
  clear: () => set((state) => ({
    commands: [],
    fillHover: null,
    selectionDragPreview: null,
    lastFailure: null,
    revision: state.revision + 1,
  })),
}));

export function enqueueModuleAddCommand(
  payload: ModuleDropPayload,
  route: ModuleAddRoute,
  identity?: SceneCommandIdentity | null,
) {
  return useSceneCommandStore.getState().enqueue({ kind: 'module.add', route, payload }, identity);
}

export function enqueueModuleReplaceCommand(
  payload: ModuleDropPayload,
  identity?: SceneCommandIdentity | null,
) {
  return useSceneCommandStore.getState().enqueue({ kind: 'module.replace', payload }, identity);
}

export function enqueueFillPointerDrop(
  detail: FillOpPointerDropDetail,
  identity?: SceneCommandIdentity | null,
) {
  return useSceneCommandStore.getState().enqueue({ kind: 'fill.pointer-drop', detail }, identity);
}

export function publishFillPointerHover(detail: FillOpPointerHoverDetail | null) {
  useSceneCommandStore.getState().setFillHover(detail);
}

export function publishSelectionDragPreview(detail: SelectionDragPreview | null) {
  useSceneCommandStore.getState().setSelectionDragPreview(detail);
}

export function enqueueRenderCapture(
  request: Omit<CaptureRequest, 'id'>,
  photoSet = false,
  identity?: SceneCommandIdentity | null,
) {
  return useSceneCommandStore.getState().enqueue({ kind: 'render.capture', request, photoSet }, identity);
}

export function enqueueViewSnapshot(
  label: string,
  projection: string,
  identity?: SceneCommandIdentity | null,
) {
  return useSceneCommandStore.getState().enqueue({ kind: 'view.snapshot', label, projection }, identity);
}

export function enqueueViewRestore(viewId: string, identity?: SceneCommandIdentity | null) {
  return useSceneCommandStore.getState().enqueue({ kind: 'view.restore', viewId }, identity);
}

export function enqueueViewRemove(viewId: string, identity?: SceneCommandIdentity | null) {
  return useSceneCommandStore.getState().enqueue({ kind: 'view.remove', viewId }, identity);
}

onAuthenticatedSessionChange(() => useSceneCommandStore.getState().clear());

export type { FillOpDropPayload };
